import React, { useState } from 'react';
import CyberBadge from '../common/CyberBadge';
import CityPoolHeader from '../common/CityPoolHeader';
import { MapPin, Plus, Radio, Eye, RadioReceiver, Sparkles, Flame, ShieldCheck, Zap } from 'lucide-react';

export default function LokaMapView({ rooms = [], onSelectDrawer, onOpenCreateModal }) {
  const [activeTab, setActiveTab] = useState('parties');
  const [selectedCity, setSelectedCity] = useState('Indore');

  // Filter hotzones, rap events, and Warm Home / Wormhole Parties
  const lokaHotzones = rooms.filter(
    (r) => r.moduleType === 'loka' || r.categoryType === 'hotzone' || r.categoryType === 'cypher' || r.categoryType === 'studio'
  );
  const rapEvents = rooms.filter((r) => r.categoryType === 'rap_event' || r.categoryType === 'jam');
  const warmHomeParties = rooms.filter(
    (r) => r.categoryType === 'wormhole_party' || r.categoryType === 'party' || r.moduleData?.isWormhole || r.isWormhole
  );

  // Filter display by selected city
  const filteredByCityParties = warmHomeParties.filter(
    (r) => (r.city && r.city.toLowerCase().includes(selectedCity.toLowerCase())) ||
           (r.location?.city && r.location.city.toLowerCase().includes(selectedCity.toLowerCase()))
  );
  const filteredByCityHotzones = lokaHotzones.filter(
    (r) => r.city && r.city.toLowerCase().includes(selectedCity.toLowerCase())
  );

  const displayParties = filteredByCityParties.length > 0 ? filteredByCityParties : (warmHomeParties.length > 0 ? warmHomeParties : [
    {
      _id: 'wh-p1',
      title: 'VIP Midnight Rooftop Warm Home Party',
      city: 'Indore, MP',
      price: 499,
      priceType: 'ticket',
      categoryType: 'wormhole_party',
      status: 'approved',
      description: 'Late-night VIP Underground Party (11:30 PM - 5:00 AM) with live DJ, trap cyphers & lounge passes.',
      moduleData: { isWormhole: true, isAdminManaged: true, eventTime: '11:30 PM - 5:00 AM' }
    },
    {
      _id: 'wh-p2',
      title: 'Indore Secret 3 AM Cypher House',
      city: 'Indore, MP',
      price: 299,
      priceType: 'ticket',
      categoryType: 'wormhole_party',
      status: 'approved',
      description: 'Secret underground bass party hosted live on RapMap radar.',
      moduleData: { isWormhole: true, isAdminManaged: true, eventTime: '1:00 AM - 6:00 AM' }
    }
  ]);

  const displayHotzones = (filteredByCityHotzones.length > 0 ? filteredByCityHotzones : lokaHotzones).length > 0
    ? (filteredByCityHotzones.length > 0 ? filteredByCityHotzones : lokaHotzones)
    : [
        {
          _id: 'hz-101',
          title: 'Vijay Nagar Trap House',
          city: 'Indore, MP',
          price: 1000,
          priceType: 'hour',
          categoryType: 'hotzone',
          status: 'approved',
        },
        {
          _id: 'hz-102',
          title: 'Indore Freestyle Records Studio',
          city: 'Indore, MP',
          price: 500,
          priceType: 'hour',
          categoryType: 'studio',
          status: 'approved',
        },
      ];

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header Banner */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MapPin size={28} style={{ color: 'var(--neon-cyan)' }} /> Rap Map & Warm Home Parties
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Upload, manage, and feature Warm Home Parties, late-night VIP events, and underground cypher hotzones.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-purple"
            style={{ padding: '10px 20px', borderRadius: '10px', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => onOpenCreateModal('loka', 'wormhole_party')}
          >
            <Flame size={18} /> Upload Warm Home Party
          </button>

          <button
            className="btn btn-cyan"
            style={{ padding: '10px 20px', borderRadius: '10px', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => onOpenCreateModal('loka', 'hotzone')}
          >
            <Plus size={16} /> Add Rap Hotzone Location
          </button>
        </div>
      </div>

      {/* CITIES SECTION & CITY POOL HEADER */}
      <CityPoolHeader
        activeCity={selectedCity}
        onCityChange={(city) => setSelectedCity(city)}
      />

      {/* Category Pills */}
      <div className="subnav-pills">
        <button
          className={`subnav-pill ${activeTab === 'parties' ? 'active' : ''}`}
          onClick={() => setActiveTab('parties')}
          style={{ position: 'relative' }}
        >
          <Flame size={15} style={{ color: '#A78BFA' }} /> 🎉 Warm Home Parties ({displayParties.length})
        </button>

        <button
          className={`subnav-pill ${activeTab === 'hotzones' ? 'active' : ''}`}
          onClick={() => setActiveTab('hotzones')}
        >
          <Radio size={15} /> Cypher Hotzones ({displayHotzones.length})
        </button>

        <button
          className={`subnav-pill ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          <Sparkles size={15} /> Rap Events & Jams ({rapEvents.length})
        </button>
      </div>

      {/* WARM HOME PARTIES SECTION */}
      {activeTab === 'parties' && (
        <div className="cyber-list-container">
          {displayParties.map((item, idx) => (
            <div
              key={item._id}
              className="cyber-list-row list-row-animate"
              style={{
                animationDelay: `${idx * 0.08}s`,
                borderLeft: '4px solid #8B5CF6',
                background: 'rgba(139, 92, 246, 0.06)',
              }}
            >
              {/* Left Info Column */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(139, 92, 246, 0.2)',
                    border: '1px solid rgba(139, 92, 246, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 16px rgba(139, 92, 246, 0.3)',
                  }}
                >
                  <Flame size={24} style={{ color: '#A78BFA' }} />
                </div>
                <div>
                  <div style={{ fontSize: '1.08rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.title}
                    <CyberBadge status={item.status || 'approved'} />
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: 'rgba(139, 92, 246, 0.25)',
                        border: '1px solid #8B5CF6',
                        color: '#A78BFA',
                        fontSize: '0.68rem',
                        fontWeight: 900,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <ShieldCheck size={12} /> ADMIN MANAGED
                    </span>
                  </div>

                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span>📍 {item.city || item.location?.city || 'Indore, MP'}</span>
                    <span>•</span>
                    <span>🕒 {item.moduleData?.eventTime || item.eventTime || '11:00 PM - 5:00 AM'}</span>
                    <span>•</span>
                    <span className="badge badge-purple" style={{ fontSize: '0.68rem' }}>
                      🌀 WORMHOLE VIP PARTY
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Action Column */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#A78BFA', fontFamily: 'var(--font-mono)' }}>
                    ₹{item.price} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {item.priceType || 'ticket'}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--neon-green)', marginTop: '2px', fontWeight: 700 }}>
                    ● LIVE ON MAP RADAR
                  </div>
                </div>

                <button
                  className="btn btn-outline"
                  style={{ padding: '8px 14px', fontSize: '0.8rem' }}
                  onClick={() => onSelectDrawer(item, 'room')}
                >
                  <Eye size={14} /> Manage Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SLEEK HIGH-TECH LIST VIEW FOR HOTZONES */}
      {activeTab === 'hotzones' && (
        <div className="cyber-list-container">
          {displayHotzones.map((item, idx) => (
            <div
              key={item._id}
              className="cyber-list-row list-row-animate"
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              {/* Left Info Column */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    background: 'rgba(0, 240, 255, 0.12)',
                    border: '1px solid rgba(0, 240, 255, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 12px rgba(0, 240, 255, 0.2)',
                  }}
                >
                  <MapPin size={20} style={{ color: 'var(--neon-cyan)' }} />
                </div>
                <div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.title}
                    <CyberBadge status={item.status || 'approved'} />
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📍 {item.city || 'Indore, MP'}</span>
                    <span>•</span>
                    <span className="badge badge-purple" style={{ fontSize: '0.65rem' }}>
                      {item.categoryType ? item.categoryType.toUpperCase() : 'HOTZONE'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Action Column */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                    ₹{item.price} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {item.priceType || 'hour'}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>Live Streaming</div>
                </div>

                <button
                  className="btn btn-outline"
                  style={{ padding: '8px 14px', fontSize: '0.8rem' }}
                  onClick={() => onSelectDrawer(item, 'room')}
                >
                  <Eye size={14} /> View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'events' && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          No underground rap events scheduled currently. Click "Add Rap Hotzone Location" to host a new cypher!
        </div>
      )}
    </div>
  );
}
